// CRM on a client's page (Blue Market only): contacts and the activity timeline.
import { useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Menu, Modal, ago, formatDate, formatDateTime, initials, useConfirm, useToast } from '../ui';
import type { Activity, ActivityKind, Client, Consent, Contact } from '../types';
import { RichTextarea } from './RichText';
import { EmailBar } from './Email';

const CONSENT_LABEL: Record<Consent, string> = { unknown: 'Not asked', granted: 'Agreed to marketing', refused: 'Declined marketing', withdrawn: 'Withdrew consent' };
const CONSENT_BADGE: Record<Consent, string> = { unknown: '', granted: 'ok', refused: 'warn', withdrawn: 'warn' };
const KIND_LABEL: Record<ActivityKind, string> = { note: 'Note', call: 'Call', meeting: 'Meeting', email: 'Email' };
const KIND_ICON: Record<ActivityKind, string> = { note: '✎', call: '☏', meeting: '◎', email: '✉' };

export function useContacts(clientId: string) {
  return useQuery({ queryKey: ['contacts', clientId], queryFn: () => get<Contact[]>(`/api/clients/${clientId}/contacts`) });
}

// ---------- contacts ----------

export function ContactsTab({ client }: { client: Client }) {
  const { can } = useAuth();
  const { data: contacts = [], isLoading } = useContacts(client.id);
  const [showLeft, setShowLeft] = useState(false);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const current = contacts.filter((c) => !c.archived), left = contacts.filter((c) => c.archived);
  return (
    <section className="card">
      <div className="card-head">
        <h2>Contacts</h2>
        <span className="grow" />
        {can('crm.edit') && <button className="btn sm" onClick={() => setEditing('new')}>Add a contact</button>}
      </div>
      {(showLeft ? [...current, ...left] : current).map((c) => (
        <button key={c.id} className={`contact-row ${c.archived ? 'left' : ''}`} onClick={() => setEditing(c)}>
          <span className="avatar sm">{initials(c.name)}</span>
          <span className="grow" style={{ minWidth: 0 }}>
            <span className="row wrap" style={{ gap: 6 }}>
              <b className="small">{c.name}</b>
              {c.primary && <span className="badge brand tiny-badge">Main contact</span>}
              {c.archived && <span className="badge tiny-badge">Left the company</span>}
            </span>
            <span className="muted tiny ellipsis">{[c.title, c.email, c.phone].filter(Boolean).join(' · ') || 'No details yet'}</span>
          </span>
          {c.marketing_consent !== 'unknown' && <span className={`badge tiny-badge ${CONSENT_BADGE[c.marketing_consent]}`}>{CONSENT_LABEL[c.marketing_consent]}</span>}
        </button>
      ))}
      {left.length > 0 && <button className="btn ghost sm block" style={{ borderRadius: 0, height: 38 }} onClick={() => setShowLeft(!showLeft)}>{showLeft ? 'Hide' : 'Show'} {left.length} who left</button>}
      {!isLoading && !contacts.length && <div className="empty">No contacts yet. Add the people you deal with at {client.name}.</div>}
      {editing && <ContactDialog client={client} contact={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function ContactDialog({ client, contact, onClose }: { client: Client; contact: Contact | null; onClose: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const edit = can('crm.edit');
  const [f, setF] = useState({
    name: contact?.name ?? '', title: contact?.title ?? '', email: contact?.email ?? '', phone: contact?.phone ?? '', linkedin: contact?.linkedin ?? '',
    notes: contact?.notes ?? '', source: contact?.source ?? '', primary: contact?.primary ?? false,
    marketing_consent: contact?.marketing_consent ?? 'unknown' as Consent, consent_note: contact?.consent_note ?? '',
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['contacts', client.id] }); qc.invalidateQueries({ queryKey: ['activities', client.id] }); };
  const save = useMutation({
    mutationFn: () => contact ? patch(`/api/contacts/${contact.id}`, f) : post(`/api/clients/${client.id}/contacts`, f),
    onSuccess: () => { refresh(); onClose(); toast(contact ? 'Contact saved' : 'Contact added'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const remove = async () => {
    if (!(await confirm(`Delete ${contact!.name} completely?`, {
      body: 'Use this when someone asks to be removed (POPIA). Their details are deleted, including from the audit log, and they are taken off past activities. If they simply left the company, choose "Left the company" instead.',
      ok: 'Delete permanently', danger: true }))) return;
    try { await del(`/api/contacts/${contact!.id}`); refresh(); onClose(); toast('Contact deleted'); } catch (e) { toast((e as Error).message, true); }
  };
  const field = (key: keyof typeof f, label: string, props: Record<string, unknown> = {}) => (
    <label className="field"><span>{label}</span>
      <input className="input" value={f[key] as string} disabled={!edit} onChange={(e) => setF({ ...f, [key]: e.target.value })} {...props} />
    </label>
  );
  return (
    <Modal title={contact ? contact.name : 'Add a contact'} onClose={onClose} width={600}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (f.name.trim()) save.mutate(); }}>
        <div className="grid-fields">
          {field('name', 'Name', { required: true, maxLength: 120, autoFocus: !contact })}
          {field('title', 'Role', { maxLength: 120, placeholder: 'e.g. Head of Marketing' })}
          {field('email', 'Email', { type: 'email', maxLength: 200 })}
          {field('phone', 'Phone', { type: 'tel', maxLength: 40, placeholder: '+27 …' })}
        </div>
        {field('linkedin', 'LinkedIn', { type: 'url', maxLength: 300, placeholder: 'https://www.linkedin.com/in/…' })}
        {field('source', 'Where these details came from', { maxLength: 300, placeholder: 'e.g. Their email signature, met at Loeries 2026' })}
        <label className="field"><span>Notes</span>
          <textarea className="input" rows={3} value={f.notes} disabled={!edit} maxLength={5000} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </label>
        <div className="consent-box">
          <label className="field" style={{ margin: 0 }}><span>Direct marketing (POPIA)</span>
            <select className="input" value={f.marketing_consent} disabled={!edit} onChange={(e) => setF({ ...f, marketing_consent: e.target.value as Consent })}>
              {(Object.keys(CONSENT_LABEL) as Consent[]).map((k) => <option key={k} value={k}>{CONSENT_LABEL[k]}</option>)}
            </select>
          </label>
          {f.marketing_consent !== 'unknown' && field('consent_note', 'How and when', { maxLength: 300, placeholder: 'e.g. Ticked the box on our event form' })}
          {contact?.consent_at && <span className="muted tiny">Recorded {formatDateTime(contact.consent_at)}</span>}
        </div>
        <label className="row small" style={{ gap: 8 }}><input type="checkbox" checked={f.primary} disabled={!edit} onChange={(e) => setF({ ...f, primary: e.target.checked })} />Main contact at {client.name}</label>
        {contact && <ContactHistory clientId={client.id} contactId={contact.id} />}
        <div className="row wrap" style={{ gap: 8 }}>
          {contact && edit && <>
            <button type="button" className="btn ghost sm" onClick={async () => { await patch(`/api/contacts/${contact.id}`, { archived: !contact.archived }); refresh(); onClose(); }}>
              {contact.archived ? 'Back at the company' : 'Left the company'}</button>
            <button type="button" className="btn ghost sm danger" onClick={remove}>Delete on request…</button>
          </>}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>{edit ? 'Cancel' : 'Close'}</button>
          {edit && <button className="btn primary" disabled={!f.name.trim() || save.isPending}>{save.isPending && <span className="spin" />}Save</button>}
        </div>
        {contact && <p className="muted tiny" style={{ margin: 0 }}>Added by {contact.created_by} · {formatDate(contact.created_at)}</p>}
      </form>
      {dialog}
    </Modal>
  );
}

function ContactHistory({ clientId, contactId }: { clientId: string; contactId: string }) {
  const { data } = useQuery({ queryKey: ['activities', clientId, 'contact', contactId],
                              queryFn: () => get<{ activities: Activity[] }>(`/api/clients/${clientId}/activities?contact=${contactId}&limit=5`) });
  if (!data?.activities.length) return null;
  return (
    <div>
      <div className="tfield-label" style={{ marginBottom: 4 }}>Recent with them</div>
      {data.activities.map((a) => <div key={a.id} className="small"><span className="muted">{KIND_ICON[a.kind]} {formatDate(a.occurred_at)}</span> {a.subject || KIND_LABEL[a.kind]}</div>)}
    </div>
  );
}

// ---------- activity ----------

export function ActivityTab({ client }: { client: Client }) {
  const { can } = useAuth();
  const { data: contacts = [] } = useContacts(client.id);
  const query = useInfiniteQuery({
    queryKey: ['activities', client.id],
    queryFn: ({ pageParam }) => get<{ activities: Activity[]; more: boolean }>(`/api/clients/${client.id}/activities${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: '',
    getNextPageParam: (last) => last.more ? last.activities.at(-1)?.occurred_at : undefined,
  });
  const items = query.data?.pages.flatMap((p) => p.activities) ?? [];
  return (
    <div className="stack" style={{ gap: 14 }}>
      <EmailBar client={client} contacts={contacts} />
      {can('crm.edit') && <ActivityComposer client={client} contacts={contacts.filter((c) => !c.archived)} />}
      <section className="card timeline">
        {items.map((a) => <ActivityItem key={a.id} a={a} client={client} contacts={contacts} />)}
        {query.hasNextPage && <button className="btn ghost sm block" style={{ borderRadius: 0, height: 38 }} onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>Show older</button>}
        {!query.isLoading && !items.length && <div className="empty">Nothing logged yet. Notes, calls, meetings and emails with {client.name} build up here.</div>}
      </section>
    </div>
  );
}

function PeoplePicker({ contacts, value, onChange }: { contacts: Contact[]; value: string[]; onChange: (ids: string[]) => void }) {
  if (!contacts.length) return null;
  return (
    <div className="people-pick" role="group" aria-label="With">
      {contacts.map((c) => (
        <button key={c.id} type="button" className={`fopt ${value.includes(c.id) ? 'on' : ''}`} aria-pressed={value.includes(c.id)}
                onClick={() => onChange(value.includes(c.id) ? value.filter((x) => x !== c.id) : [...value, c.id])}>{c.name}</button>
      ))}
    </div>
  );
}

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

function ActivityComposer({ client, contacts }: { client: Client; contacts: Contact[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const blank = { kind: 'note' as ActivityKind, subject: '', body: '', contact_ids: [] as string[], when: '' };
  const [f, setF] = useState(blank);
  const [open, setOpen] = useState(false);
  const add = useMutation({
    mutationFn: () => post(`/api/clients/${client.id}/activities`, { kind: f.kind, subject: f.subject, body: f.body, contact_ids: f.contact_ids,
                                                                    occurred_at: f.when ? new Date(f.when).toISOString() : null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['activities', client.id] }); setF(blank); setOpen(false); toast(`${KIND_LABEL[f.kind]} logged`); },
    onError: (e: Error) => toast(e.message, true),
  });
  if (!open) return <button className="composer-collapsed" onClick={() => setOpen(true)}>Log a note, call, meeting or email…</button>;
  return (
    <form className="card pad stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); if (f.subject.trim() || f.body.trim()) add.mutate(); }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <div className="seg" role="group" aria-label="Kind">
          {(Object.keys(KIND_LABEL) as ActivityKind[]).map((k) => <button key={k} type="button" className={f.kind === k ? 'on' : ''} onClick={() => setF({ ...f, kind: k })}>{KIND_LABEL[k]}</button>)}
        </div>
        <input type="datetime-local" className="input compact" value={f.when || nowLocal()} max={nowLocal()} onChange={(e) => setF({ ...f, when: e.target.value })} aria-label="When" />
      </div>
      {f.kind !== 'note' && <input className="input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={200} placeholder={`What the ${f.kind} was about`} />}
      <RichTextarea value={f.body} onChange={(body) => setF((x) => ({ ...x, body }))} rows={3} maxLength={50000} autoFocus
                    placeholder={f.kind === 'note' ? 'What happened, what was said, what comes next' : 'Notes'} onSubmit={() => add.mutate()} onCancel={() => setOpen(false)} />
      {contacts.length > 0 && <div><div className="tfield-label" style={{ marginBottom: 4 }}>With</div><PeoplePicker contacts={contacts} value={f.contact_ids} onChange={(contact_ids) => setF({ ...f, contact_ids })} /></div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn ghost sm" onClick={() => { setF(blank); setOpen(false); }}>Cancel</button>
        <button className="btn sm primary" disabled={(!f.subject.trim() && !f.body.trim()) || add.isPending}>{add.isPending && <span className="spin" />}Log {KIND_LABEL[f.kind].toLowerCase()}</button>
      </div>
    </form>
  );
}

function ActivityItem({ a, client, contacts }: { a: Activity; client: Client; contacts: Contact[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<{ subject: string; body: string; contact_ids: string[] } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['activities', client.id] });
  const save = async () => { try { await patch(`/api/activities/${a.id}`, editing); setEditing(null); refresh(); } catch (e) { toast((e as Error).message, true); } };
  const remove = async () => {
    if (!(await confirm('Delete this entry?', { ok: 'Delete', danger: true }))) return;
    try { await del(`/api/activities/${a.id}`); refresh(); } catch (e) { toast((e as Error).message, true); }
  };
  const people = useMemo(() => contacts.filter((c) => !c.archived || a.contacts.some((x) => x.id === c.id)), [contacts, a]);
  return (
    <div className={`activity kind-${a.kind}`}>
      <span className="activity-icon" title={KIND_LABEL[a.kind]} aria-hidden="true">{KIND_ICON[a.kind]}</span>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="row wrap" style={{ gap: '2px 8px' }}>
              <b className="small">{a.subject || KIND_LABEL[a.kind]}</b>
              {a.contacts.length > 0 && <span className="muted tiny">with {a.contacts.map((c) => c.name).join(', ')}</span>}
            </div>
            <div className="muted tiny" title={formatDateTime(a.occurred_at)}>{a.author} · {ago(a.occurred_at)}{a.edited ? ' · edited' : ''}</div>
          </div>
          {a.can_edit && !editing && <Menu label="Entry actions" items={[{ label: 'Edit', onClick: () => setEditing({ subject: a.subject, body: a.body, contact_ids: a.contacts.map((c) => c.id) }) },
                                                                        { label: 'Delete', danger: true, onClick: remove }]} />}
        </div>
        {editing ? (
          <div className="stack" style={{ gap: 6, marginTop: 6 }}>
            <input className="input compact" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} maxLength={200} placeholder="Subject" />
            <RichTextarea value={editing.body} onChange={(body) => setEditing((x) => x && { ...x, body })} rows={4} onSubmit={save} onCancel={() => setEditing(null)} />
            <PeoplePicker contacts={people} value={editing.contact_ids} onChange={(contact_ids) => setEditing({ ...editing, contact_ids })} />
            <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
              <button className="btn sm ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn sm primary" onClick={save}>Save</button>
            </div>
          </div>
        ) : a.body_html && <div className="comment-text rich-text" style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: a.body_html }} />}
      </div>
      {dialog}
    </div>
  );
}

/** Two small cards on a client's overview: the main contacts and the latest activity. */
export function CrmGlance({ client, go }: { client: Client; go: (tab: string) => void }) {
  const { data: contacts = [] } = useContacts(client.id);
  const { data: recent } = useQuery({ queryKey: ['activities', client.id, 'recent'], queryFn: () => get<{ activities: Activity[] }>(`/api/clients/${client.id}/activities?limit=3`) });
  const people = contacts.filter((c) => !c.archived).slice(0, 4);
  const card = (title: string, tab: string, body: ReactNode) => (
    <section className="card pad stack" style={{ gap: 8 }}>
      <div className="row"><h2 className="grow">{title}</h2><button className="link-btn small" onClick={() => go(tab)}>All</button></div>
      {body}
    </section>
  );
  return (
    <>
      {card('Contacts', 'contacts', people.length ? people.map((c) => (
        <div key={c.id} className="row" style={{ gap: 8 }}><span className="avatar sm">{initials(c.name)}</span>
          <span className="grow" style={{ minWidth: 0 }}><b className="small">{c.name}</b>{c.primary && <span className="muted tiny"> · main contact</span>}<br /><span className="muted tiny">{c.title || c.email || ''}</span></span></div>
      )) : <span className="muted small">No contacts yet.</span>)}
      {card('Latest activity', 'activity', recent?.activities.length ? recent.activities.map((a) => (
        <div key={a.id} className="small"><span className="muted">{KIND_ICON[a.kind]} {ago(a.occurred_at)}</span> · {a.subject || KIND_LABEL[a.kind]}
          {a.contacts.length > 0 && <span className="muted"> with {a.contacts.map((c) => c.name).join(', ')}</span>}</div>
      )) : <span className="muted small">Nothing logged yet.</span>)}
    </>
  );
}
